import * as THREE from "three"

import Character from "./Character"
import { sceneManager, license, api, store } from "../index"
import data from "../../data/adjustable_mannequin.json"
import { quickSave, openSaveBodyTypePopup, applyBodyType } from "../helpers/SaveManager"
import { connected } from "../helpers/Helpers"
import { showMessage } from "../Message"
import { t } from "../helpers/Translate"
import { isScaleBarFor, updateScaleBar } from "../helpers/ScaleBar"

const GUIDE_LINES_OPACITY = 0.3
// Blender sliders that aren't exposed in the app (fixed at 0)
const HIDDEN_SLIDERS = ["head style", "muscle group control", "guide lines control"]
// App-only sliders: head/hand/foot size relative to the body (scale of the
// bones after cancelling out scale inherited from the limb above them)
const EXTREMITY_SLIDERS = [
	{ name: "head size", label: "Head", min: 0.7, max: 1.5, default: 1, bones: ["mixamorigHead"] },
	{ name: "hands size", label: "Hands", min: 0.6, max: 2, default: 1, bones: ["mixamorigLeftHand", "mixamorigRightHand"] },
	{ name: "feet size", label: "Feet", min: 0.6, max: 2, default: 1, bones: ["mixamorigLeftFoot", "mixamorigRightFoot"] },
]
const extremityBones = EXTREMITY_SLIDERS.flatMap((slider) => slider.bones)
// App-only sliders: stretch a segment along its length without thickening it.
// Bone frames match the Mixamo rig (world-aligned at rest), so arms run along
// local X and the spine/legs along local Y.
const sides = (name) => [`mixamorigLeft${name}`, `mixamorigRight${name}`]
const LENGTH_SLIDERS = [
	{ name: "torso length", label: "Torso length", bones: ["mixamorigSpine", "mixamorigSpine1", "mixamorigSpine2"], axis: "y" },
	{ name: "neck length", label: "Neck length", bones: ["mixamorigNeck"], axis: "y" },
	{ name: "upper arm length", label: "Upper arm length", bones: sides("Arm"), axis: "x" },
	{ name: "forearm length", label: "Forearm length", bones: sides("ForeArm"), axis: "x" },
	{ name: "thigh length", label: "Thigh length", bones: sides("UpLeg"), axis: "y" },
	{ name: "shin length", label: "Shin length", bones: sides("Leg"), axis: "y" },
].map((slider) => ({ ...slider, min: 0.7, max: 1.4, default: 1 }))
// Same stretch as the length sliders, but across the head (local X)
const STRETCH_SLIDERS = [
	...LENGTH_SLIDERS,
	{ name: "head width", label: "Head width", bones: ["mixamorigHead"], axis: "x", min: 0.8, max: 1.25, default: 1 },
]
// Panel layout, head to toe
const PANEL_GROUPS = [
	["Head & neck", ["head size", "head width", "neck length", "neck control"]],
	["Shoulders & chest", ["shoulders control", "shoulder angle", "breasts control", "breasts angle"]],
	["Arms", ["arms control", "upper arm length", "forearm length", "hands size"]],
	["Torso & hips", ["torso length", "torso control", "belly control", "hips control"]],
	["Legs", ["legs control", "thigh length", "shin length", "feet size"]],
]
const PANEL_SLIDERS = [
	...STRETCH_SLIDERS,
	...data.sliders.filter((slider) => !HIDDEN_SLIDERS.includes(slider.name)),
	...EXTREMITY_SLIDERS,
]

let guideLinesTexture = null
const getGuideLinesTexture = () => {
	if (!guideLinesTexture) {
		guideLinesTexture = new THREE.TextureLoader().load("images/adjustable/guide_lines.webp")
		guideLinesTexture.flipY = false // glTF UV convention
		guideLinesTexture.colorSpace = THREE.SRGBColorSpace
	}
	return guideLinesTexture
}

const presetSliders = (info) => {
	const { sliders, jointScales } = data.presets[info.preset]
	// Preset foot scales are local to the (scaled) thigh; the sliders are body-relative
	const hands = jointScales.mixamorigLeftHand || 1
	const feet = (jointScales.mixamorigLeftFoot || 1) * (jointScales.mixamorigLeftUpLeg || 1)
	const head = info.defaultJointScales?.mixamorigHead ?? jointScales.mixamorigHead ?? 1
	return { ...sliders, "head size": head, "hands size": hands, "feet size": feet, ...info.defaultSliders }
}

const sampleCurve = ({ x0, x1, values }, x) => {
	const f = THREE.MathUtils.clamp((x - x0) / (x1 - x0), 0, 1) * (values.length - 1)
	const i = Math.min(Math.floor(f), values.length - 2)
	return THREE.MathUtils.lerp(values[i], values[i + 1], f - i)
}

// Blender driver -> function(sliderValues) returning the driven value
const compileDriver = ({ expr, vars, curve }) => {
	const names = Object.keys(vars)
	const fn = new Function(...names, `return ${expr}`)
	return (values) => sampleCurve(curve, fn(...names.map((name) => values[vars[name]] || 0)))
}

const shapeKeyDrivers = data.shapeKeys.map((shapeKey) => ({
	...shapeKey,
	evaluate: compileDriver(shapeKey),
}))

class AdjustableCharacter extends Character {
	constructor(options) {
		const preset = data.presets[options.info.preset]
		const sliders = { ...presetSliders(options.info), ...options.sliders }
		const jointScales = options.jointScales || Object.fromEntries(
			Object.entries({ ...preset.jointScales, ...options.info.defaultJointScales })
				.filter(([name]) => !extremityBones.includes(name))
				.map(([name, s]) => [name, { x: s, y: s, z: s }])
		)
		const scale = options.scale || new THREE.Vector3().setScalar(options.info.defaultScale || 1)
		super({ ...options, jointScales, scale })
		this.isAdjustable = true
		this.sliders = sliders
	}

	setMaterial() {
		if (!this.mesh) {
			this.setupMannequin()
			this.setupSliders()
		}
		this.uniforms.baseColor.value.set(this.color)
	}

	sliderValue(slider) {
		return this.sliders[slider.name] ?? slider.default
	}

	setupMannequin() {
		this.object.traverse((child) => {
			if (child.isSkinnedMesh) this.mesh = child
		})
		this.mesh.castShadow = true
		this.mesh.receiveShadow = true
		this.mesh.frustumCulled = false

		this.uniforms = {
			baseColor: { value: new THREE.Color(this.color) },
		}
		const material = new THREE.MeshPhongMaterial({ map: getGuideLinesTexture() })
		material.onBeforeCompile = (shader) => {
			Object.assign(shader.uniforms, this.uniforms)
			shader.fragmentShader = shader.fragmentShader
				.replace(
					"#include <map_pars_fragment>",
					"#include <map_pars_fragment>\nuniform vec3 baseColor;"
				)
				.replace(
					"#include <map_fragment>",
					`vec3 guideTexel = texture2D(map, vMapUv).rgb;
					diffuseColor.rgb = baseColor * mix(vec3(1.0), guideTexel, ${GUIDE_LINES_OPACITY.toFixed(2)});`
				)
		}
		this.mesh.material = material

		// The rig's spine starts exactly at the hips, so the spine joint would
		// cover the hips joint; show the hips joint a bit lower instead
		const hipsJoint = this.joints.find((joint) => joint.name === "mixamorigHips")
		hipsJoint.position.y = -this.object.getObjectByName("mixamorigSpine1").position.y / 3

		// Bone-driven sliders (shoulder width, shoulder angle) were baked into
		// one clip per slider, keyed evenly across the slider's range
		this.boneSliders = []
		this.animations.forEach((clip) => {
			const sliderName = clip.name.replace(/^slider:/, "")
			const slider = data.sliders.find((s) => s.name === sliderName)
			if (!slider) return
			const restTime = ((0 - slider.min) / (slider.max - slider.min)) * clip.duration
			clip.tracks.forEach((track) => {
				const [boneName, property] = track.name.split(".")
				const bone = this.object.getObjectByName(boneName)
				const isJoint = this.joints.some((joint) => joint.parent === bone)
				// Joints keep their user rotation/scale; sliders only move them
				if (!bone || (isJoint && property !== "position")) return
				const interpolant = track.createInterpolant()
				const at = (time) => Array.from(interpolant.evaluate(time))
				const values = track.values
				const stride = values.length / track.times.length
				const varies = track.times.some((_, i) =>
					values.slice(i * stride, i * stride + stride).some((v, k) => Math.abs(v - values[k]) > 1e-5)
				)
				if (!varies) return
				this.boneSliders.push({
					slider, bone, property, at, restTime, duration: clip.duration,
					restValue: at(restTime),
				})
			})
		})
	}

	setupSliders() {
		// Length/width sliders: each stretched bone gets a child proxy that
		// carries the stretch and stands in for the bone in the skeleton. The
		// mesh stretches, but the bone's children (and the user's joints) don't
		// inherit the non-uniform scale; they're moved out along the segment
		// instead.
		this.stretchProxies = new Map()
		const stretched = new Map(STRETCH_SLIDERS.flatMap((slider) => slider.bones.map((name) => [name, slider])))
		this.object.traverse((mesh) => {
			if (!mesh.isSkinnedMesh) return
			const bones = mesh.skeleton.bones
			bones.forEach((bone, i) => {
				const slider = stretched.get(bone.name)
				if (!slider) return
				if (!this.stretchProxies.has(bone)) {
					const proxy = new THREE.Object3D()
					proxy.name = `${bone.name}_stretch`
					bone.add(proxy)
					this.stretchProxies.set(bone, { proxy, slider })
				}
				bones[i] = this.stretchProxies.get(bone).proxy
			})
		})
		// Head/hands/feet keep the model's own size at 1
		this.extremityRest = new Map()
		EXTREMITY_SLIDERS.forEach((slider) => {
			slider.bones.forEach((name) => {
				const bone = this.object.getObjectByName(name)
				this.extremityRest.set(bone, { slider, scale: bone.scale.x, inherited: this.inheritedScale(bone), applied: bone.scale.x })
			})
		})
		// The hips position is the character's position, so it's left alone
		this.restPositions = new Map()
		this.object.traverse((child) => {
			if (child.isBone && child !== this.hip) this.restPositions.set(child, child.position.clone())
		})

		this.applySliders()
	}

	// Scale a bone inherits from the limb above it, up to the hips
	inheritedScale(bone) {
		let inherited = 1
		for (let parent = bone.parent; parent && parent !== this.hip; parent = parent.parent) {
			inherited *= parent.scale.x
		}
		return inherited
	}

	setSliders(sliders) {
		this.sliders = { ...sliders }
		this.applySliders()
		if (panelCharacter === this) {
			panelCharacter = null
			showAdjustablePanel(this)
		}
	}

	setSlider(name, value) {
		// Keep the lowest point where it was (e.g. feet on the ground when
		// changing leg length)
		const lowest = () => new THREE.Box3().setFromObject(this.getBoundingObject()).min.y
		const before = lowest()
		this.sliders[name] = value
		this.applySliders()
		this.base.position.y += before - lowest()
	}

	applySliders() {
		if (!this.stretchProxies) return
		const dictionary = this.mesh.morphTargetDictionary
		shapeKeyDrivers.forEach((driver) => {
			const value = THREE.MathUtils.clamp(driver.evaluate(this.sliders), driver.min, driver.max)
			this.mesh.morphTargetInfluences[dictionary[driver.key]] = value
		})

		this.extremityRest.forEach((rest, bone) => {
			// The user's own joint scale (scale gizmo) sits on top of the slider's
			const userScale = bone.scale.clone().divideScalar(rest.applied)
			rest.applied = (rest.scale * this.sliderValue(rest.slider) * rest.inherited) / this.inheritedScale(bone)
			bone.scale.copy(userScale).multiplyScalar(rest.applied)
		})

		this.stretchProxies.forEach(({ proxy, slider }) => {
			proxy.scale.set(1, 1, 1)
			proxy.scale[slider.axis] = this.sliderValue(slider)
		})

		const offsets = new Map()
		;(this.boneSliders || []).forEach(({ slider, bone, property, at, duration, restValue }) => {
			const value = this.sliders[slider.name] || 0
			const time = ((value - slider.min) / (slider.max - slider.min)) * duration
			const sampled = at(time)
			if (property === "position") {
				const offset = new THREE.Vector3(...sampled).sub(new THREE.Vector3(...restValue))
				offsets.set(bone, offset.add(offsets.get(bone) || new THREE.Vector3()))
			} else {
				bone[property].fromArray(sampled)
			}
		})

		this.restPositions.forEach((rest, bone) => {
			bone.position.copy(rest)
			const parentStretch = this.stretchProxies.get(bone.parent)
			if (parentStretch) bone.position.multiply(parentStretch.proxy.scale)
			if (offsets.has(bone)) bone.position.add(offsets.get(bone))
		})
		sceneManager.requestRender()
	}

	setScale(scale) {
		// No tween: placing on the ground right after load needs the final scale
		if (scale) this.base.scale.copy(scale)
		if (isScaleBarFor(this)) updateScaleBar(this)
	}

	// Head/hands/feet joint scales are saved as the user's scale on top of the
	// size sliders, so they survive slider changes and undo
	getJointScales() {
		const jointScales = super.getJointScales()
		this.extremityRest?.forEach((rest, bone) => {
			const { x, y, z } = bone.scale.clone().divideScalar(rest.applied)
			jointScales[bone.name] = { x, y, z }
		})
		return jointScales
	}

	setJointScales(jointScales, animate, store) {
		// No tween: the hands/feet sliders need the final limb scales
		super.setJointScales(jointScales, false, store)
		this.extremityRest?.forEach((rest, bone) => {
			if (jointScales?.[bone.name]) rest.applied = 1
		})
		this.applySliders()
	}

	toggleProportionsPanel() {
		if (!license.valid) {
			license.check()
			return
		}
		if (panel && panel.classList.contains("is-active") && panelCharacter === this) {
			showAdjustablePanel(null)
		} else {
			this.selectCharacter()
			sceneManager.setGizmo("scale")
			showAdjustablePanel(this)
		}
	}

	delete(store = true) {
		super.delete(store)
		showAdjustablePanel(null)
	}
}

// ------------------------------------------------------------------ panel

let panel = null
let panelCharacter = null

// While the panel is open it follows the selected character
export const followSelection = (subject) => {
	if (panel && panel.classList.contains("is-active")) showAdjustablePanel(subject)
}

// Redraw the open panel (e.g. after the saved presets change)
export const refreshAdjustablePanel = () => {
	if (!panelCharacter) return
	const subject = panelCharacter
	panelCharacter = null
	showAdjustablePanel(subject)
}

export const showAdjustablePanel = (subject) => {
	if (!subject || !subject.isAdjustable || !subject.alive) {
		if (panel) panel.classList.remove("is-active")
		panelCharacter = null
		return
	}
	if (!panel) {
		panel = document.createElement("div")
		panel.id = "adjustable-panel"
		document.body.appendChild(panel)
	}
	if (panelCharacter === subject && panel.classList.contains("is-active")) return
	panelCharacter = subject
	panel.innerHTML = ""

	if (license.valid) addPresets()

	PANEL_GROUPS.forEach(([title, names]) => {
		const group = names.map((name) => PANEL_SLIDERS.find((slider) => slider.name === name)).filter(Boolean)
		if (!group.length) return
		const heading = document.createElement("div")
		heading.classList.add("adjustable-group")
		heading.innerText = t(title)
		panel.appendChild(heading)
		group.forEach((slider) => addRow(slider))
	})

	function addPresets() {
		const cloud = (action) => () => {
			if (!connected) {
				showMessage("Internet connection required for cloud features", 4000, 300)
			} else {
				action()
			}
		}
		const save = document.createElement("button")
		save.classList.add("button")
		save.innerText = t("Save preset")
		save.onclick = cloud(() => openSaveBodyTypePopup(subject))
		panel.appendChild(save)

		store.state.savedBodyTypes.forEach((preset) => {
			const row = document.createElement("div")
			row.classList.add("adjustable-preset")
			const apply = document.createElement("button")
			apply.innerText = preset.name
			apply.onclick = cloud(() =>
				api.getBodyType(license.key, preset.id).then((bodyType) =>
					applyBodyType(subject, preset.name, JSON.parse(bodyType.values))
				)
			)
			const remove = document.createElement("button")
			remove.innerHTML = `<svg viewBox="0 0 24 24"><use xlink:href="images/feather-sprite.svg#x" /></svg>`
			remove.title = t("Delete")
			remove.onclick = cloud(() => {
				if (!confirm(t("Delete {name}?").replace("{name}", preset.name))) return
				api.deleteBodyType(license.key, preset.id)
			})
			row.append(apply, remove)
			panel.appendChild(row)
		})
	}

	function addRow(slider) {
		const set = (value) => subject.setSlider(slider.name, value)
		const row = document.createElement("label")
		row.classList.add("adjustable-row")
		const name = document.createElement("span")
		name.innerText = t(slider.label)
		const input = document.createElement("input")
		input.type = "range"
		input.min = slider.min
		input.max = slider.max
		input.step = (slider.max - slider.min) / 200
		input.value = subject.sliderValue(slider) ?? 0
		input.onpointerdown = () => sceneManager.stateManager.storeSceneState(subject)
		input.oninput = () => set(parseFloat(input.value))
		input.onchange = () => quickSave()
		input.onkeydown = (event) => {
			if (event.key.startsWith("Arrow")) event.preventDefault()
		}
		row.append(name, input)
		panel.appendChild(row)
	}

	const reset = document.createElement("button")
	reset.classList.add("button", "small", "mint")
	reset.innerText = t("Reset sliders")
	reset.onclick = () => {
		sceneManager.stateManager.storeSceneState(subject)
		subject.sliders = presetSliders(subject.info)
		subject.applySliders()
		quickSave()
		panelCharacter = null
		showAdjustablePanel(subject)
	}
	panel.appendChild(reset)

	panel.classList.add("is-active")
}

export default AdjustableCharacter
